/**
 * What the image viewer (the lightbox, Message.tsx) shows: a channel's
 * pictures, or one person's picture (their avatar or banner, from the
 * profile card). One store, so the viewer is drawn once for the whole app.
 */
import type { Attachment, Snowflake, User } from '@neosocial/protocol';
import { create } from 'zustand';
import { assetUrl } from '../lib/config';

export interface ViewerState {
  list: Attachment[];
  index: number;
  from: HTMLElement | null;
  /**
   * A person's picture: the caption is their name (their own text, in its
   * own direction), not a file name.
   */
  person?: { name: string; kind: 'avatar' | 'banner' };
}

export const useImageViewer = create<{ open: ViewerState | null }>(() => ({ open: null }));

/** The stored avatar is the largest there is (the pipeline keeps one, up to 512 x 512). */
export const AVATAR_PX = 512;
/** A banner's stored size (5:2, up to 1600 x 640). */
export const BANNER_PX = { width: 1600, height: 640 };

/**
 * Opens a person's avatar (or banner) large. `url` is the stored image's
 * path, the same file the small avatar already loaded (cached forever), so
 * it costs no new download. False when there is no picture to show.
 */
export function openPersonImage(
  user: Pick<User, 'id'>,
  url: string | null | undefined,
  name: string,
  from: HTMLElement | null,
  kind: 'avatar' | 'banner' = 'avatar',
): boolean {
  const src = assetUrl(url ?? null);
  if (!src) return false;
  const size = kind === 'avatar' ? { width: AVATAR_PX, height: AVATAR_PX } : BANNER_PX;
  const a: Attachment = {
    id: `${kind}:${user.id}` as Snowflake,
    filename: name,
    size: 0,
    url: src,
    proxy_url: src,
    content_type: 'image/webp',
    ...size,
    description: name,
  };
  useImageViewer.setState({ open: { list: [a], index: 0, from, person: { name, kind } } });
  return true;
}
